import React, { useState, useEffect, useCallback } from "react";
import TrainingGuide from "../common/TrainingGuide";
import pricingSteps from "../../data/training/pricing";

// ── Types ─────────────────────────────────────────────────────────────────────

interface CompetitorPrice {
  source: string;
  price: number;
  url: string;
  scraped_at: string;
}

interface BrandLeverage {
  role: string;
  advantage: string;
  strategy: string;
}

interface Insight {
  market_position: "below_market" | "at_market" | "above_market" | "premium" | "unknown";
  price_score: number;
  suggested_price_aed: number | null;
  price_change_direction: "increase" | "decrease" | "maintain";
  rationale: string;
  competitive_gaps: string[];
  quick_wins: string[];
  risk_level: "low" | "medium" | "high";
  confidence: "low" | "medium" | "high";
  brand_leverage_used: boolean;
  ai_source: string;
  analysed_at: string;
}

interface PriceReport {
  id: string;
  product_id: number;
  product_title: string;
  vendor: string;
  our_price: number;
  sku: string;
  status: string;
  is_priority_brand: boolean;
  brand_leverage: BrandLeverage | null;
  competitor_prices: CompetitorPrice[];
  competitor_count: number;
  cheapest_competitor: CompetitorPrice | null;
  insight: Insight;
  created_at: string;
}

interface DashboardStats {
  total_products: number;
  priority_brands: number;
  avg_price_score: number;
  suggest_increase: number;
  suggest_decrease: number;
  suggest_maintain: number;
  below_market: number;
  at_market: number;
  above_market: number;
  premium: number;
  last_scan: string | null;
}

// ── Color / label helpers ──────────────────────────────────────────────────────

const POSITION_COLORS: Record<string, string> = {
  below_market: "#7ec8e3",
  at_market:    "#b8d58d",
  above_market: "#ffd6a5",
  premium:      "#c9a96e",
  unknown:      "#666",
};
const POSITION_LABELS: Record<string, string> = {
  below_market: "Below Market",
  at_market:    "At Market",
  above_market: "Above Market",
  premium:      "Premium",
  unknown:      "Unknown",
};
const DIRECTION_COLORS: Record<string, string> = {
  increase: "#b8d58d",
  decrease: "#ff6b6b",
  maintain: "#7ec8e3",
};
const DIRECTION_ICONS: Record<string, string> = {
  increase: "↑",
  decrease: "↓",
  maintain: "→",
};
const RISK_COLORS: Record<string, string> = {
  low:    "#b8d58d",
  medium: "#ffd6a5",
  high:   "#ff6b6b",
};
const AI_LABELS: Record<string, string> = {
  ollama:               "Ollama",
  deepseek:             "DeepSeek",
  deterministic_fallback: "Rule-based",
};

function scoreColor(score: number): string {
  if (score >= 8) return "#b8d58d";
  if (score >= 5) return "#ffd6a5";
  return "#ff6b6b";
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("en-AE", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

// ── Stat card ─────────────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string | number;
  sub?: string;
  color?: string;
  icon?: string;
}> = ({ label, value, sub, color = "#ffd6a5", icon }) => (
  <div style={{
    background: "rgba(255,255,255,0.02)",
    backdropFilter: "blur(60px)",
    WebkitBackdropFilter: "blur(60px)",
    border: "0.5px solid rgba(189,178,255,0.15)",
    borderRadius: 16,
    padding: "20px 22px",
    flex: 1,
    minWidth: 140,
  }}>
    <div style={{ fontSize: 13, color: "#9b8f83", marginBottom: 6, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase" }}>
      {icon && <span style={{ marginRight: 6 }}>{icon}</span>}{label}
    </div>
    <div style={{ fontSize: 28, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{sub}</div>}
  </div>
);

// ── Score donut ───────────────────────────────────────────────────────────────

const ScoreDonut: React.FC<{ score: number }> = ({ score }) => {
  const pct = score / 10;
  const r = 18;
  const circ = 2 * Math.PI * r;
  const color = scoreColor(score);
  return (
    <svg width={48} height={48} style={{ flexShrink: 0 }}>
      <circle cx={24} cy={24} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={4} />
      <circle
        cx={24} cy={24} r={r} fill="none"
        stroke={color} strokeWidth={4}
        strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`}
        strokeLinecap="round"
        transform="rotate(-90 24 24)"
      />
      <text x={24} y={28} textAnchor="middle" fill={color} fontSize={13} fontWeight={800}>{score}</text>
    </svg>
  );
};

// ── Report card ───────────────────────────────────────────────────────────────

const ReportCard: React.FC<{ report: PriceReport; onSelect: (r: PriceReport) => void }> = ({ report, onSelect }) => {
  const ins = report.insight;
  const posColor  = POSITION_COLORS[ins.market_position] || "#666";
  const dirColor  = DIRECTION_COLORS[ins.price_change_direction] || "#ffd6a5";
  const dirIcon   = DIRECTION_ICONS[ins.price_change_direction] || "→";

  return (
    <div
      onClick={() => onSelect(report)}
      style={{
        background: report.is_priority_brand
          ? "rgba(201,169,110,0.07)"
          : "rgba(19,27,46,0.7)",
        border: report.is_priority_brand
          ? "1px solid rgba(201,169,110,0.3)"
          : "1px solid rgba(255,214,165,0.08)",
        borderRadius: 10,
        padding: "14px 16px",
        cursor: "pointer",
        transition: "border-color 0.15s",
        display: "flex",
        gap: 14,
        alignItems: "center",
      }}
    >
      <ScoreDonut score={ins.price_score || 5} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, color: "#ffd6a5", fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>
            {report.product_title}
          </span>
          {report.is_priority_brand && (
            <span style={{ background: "rgba(201,169,110,0.18)", color: "#c9a96e", borderRadius: 4, padding: "1px 7px", fontSize: 11, fontWeight: 700 }}>
              {report.vendor.toUpperCase()}
            </span>
          )}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 5, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 13, color: "#9b8f83" }}>AED {report.our_price.toFixed(2)}</span>
          {ins.suggested_price_aed !== null && (
            <span style={{ fontSize: 13, color: dirColor, fontWeight: 700 }}>
              {dirIcon} AED {ins.suggested_price_aed.toFixed(2)}
            </span>
          )}
          <span style={{ fontSize: 11, color: posColor, background: "rgba(255,255,255,0.05)", borderRadius: 4, padding: "1px 6px" }}>
            {POSITION_LABELS[ins.market_position]}
          </span>
          {report.cheapest_competitor && (
            <span style={{ fontSize: 11, color: "#9b8f83" }}>
              Cheapest: {report.cheapest_competitor.source} AED {report.cheapest_competitor.price.toFixed(2)}
            </span>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
        <span style={{ fontSize: 11, color: RISK_COLORS[ins.risk_level], fontWeight: 600 }}>
          {ins.risk_level.toUpperCase()} RISK
        </span>
        <span style={{ fontSize: 11, color: "#555" }}>{AI_LABELS[ins.ai_source] || ins.ai_source}</span>
      </div>
    </div>
  );
};

// ── Detail drawer ─────────────────────────────────────────────────────────────

const DetailDrawer: React.FC<{ report: PriceReport; onClose: () => void }> = ({ report, onClose }) => {
  const ins = report.insight;
  return (
    <div style={{
      position: "fixed", top: 0, right: 0, width: "min(480px, 100vw)", height: "100vh",
      background: "#0e1526", borderLeft: "1px solid rgba(255,214,165,0.15)",
      overflowY: "auto", zIndex: 1000, padding: "28px 24px",
    }}>
      <button onClick={onClose} style={{
        background: "none", border: "none", color: "#9b8f83", fontSize: 22,
        cursor: "pointer", position: "absolute", top: 16, right: 20,
      }}>✕</button>

      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
          Product
        </div>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#ffd6a5", lineHeight: 1.3 }}>
          {report.product_title}
        </div>
        <div style={{ fontSize: 12, color: "#9b8f83", marginTop: 3 }}>
          {report.vendor} · SKU: {report.sku || "—"} · Analysed {fmtDate(report.created_at)}
        </div>
      </div>

      {/* Brand leverage banner */}
      {report.is_priority_brand && report.brand_leverage && (
        <div style={{
          background: "rgba(201,169,110,0.1)", border: "1px solid rgba(201,169,110,0.25)",
          borderRadius: 8, padding: "12px 14px", marginBottom: 18,
        }}>
          <div style={{ fontSize: 12, color: "#c9a96e", fontWeight: 700, marginBottom: 4 }}>
            BRAND LEVERAGE — {report.brand_leverage.role}
          </div>
          <div style={{ fontSize: 12, color: "#9b8f83" }}>{report.brand_leverage.strategy}</div>
        </div>
      )}

      {/* Price comparison */}
      <div style={{ display: "flex", gap: 12, marginBottom: 18 }}>
        <div style={{
          flex: 1, background: "rgba(255,255,255,0.04)", borderRadius: 8,
          padding: "12px 14px", textAlign: "center",
        }}>
          <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>OUR PRICE</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#ffd6a5" }}>
            AED {report.our_price.toFixed(2)}
          </div>
        </div>
        {ins.suggested_price_aed !== null && (
          <div style={{
            flex: 1, background: "rgba(255,255,255,0.04)", borderRadius: 8,
            padding: "12px 14px", textAlign: "center",
          }}>
            <div style={{ fontSize: 11, color: "#666", marginBottom: 4 }}>AI SUGGESTS</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: DIRECTION_COLORS[ins.price_change_direction] }}>
              {DIRECTION_ICONS[ins.price_change_direction]} AED {ins.suggested_price_aed.toFixed(2)}
            </div>
          </div>
        )}
      </div>

      {/* Insight metrics */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ScoreDonut score={ins.price_score || 5} />
          <div>
            <div style={{ fontSize: 11, color: "#666" }}>PRICE SCORE</div>
            <div style={{ fontSize: 13, color: scoreColor(ins.price_score || 5), fontWeight: 700 }}>
              {ins.price_score}/10
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontSize: 12, color: POSITION_COLORS[ins.market_position] }}>
            {POSITION_LABELS[ins.market_position]}
          </span>
          <span style={{ fontSize: 12, color: RISK_COLORS[ins.risk_level] }}>
            {ins.risk_level.toUpperCase()} RISK
          </span>
          <span style={{ fontSize: 11, color: "#555" }}>
            Confidence: {ins.confidence}
          </span>
        </div>
      </div>

      {/* Rationale */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
          AI Rationale
        </div>
        <div style={{ fontSize: 13, color: "#c4b49f", lineHeight: 1.6, background: "rgba(255,255,255,0.03)", borderRadius: 8, padding: "10px 12px" }}>
          {ins.rationale}
        </div>
        <div style={{ fontSize: 11, color: "#444", marginTop: 4 }}>
          Powered by: {AI_LABELS[ins.ai_source] || ins.ai_source}
        </div>
      </div>

      {/* Competitor prices */}
      {report.competitor_prices.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            Competitor Prices
          </div>
          {report.competitor_prices.map((c, i) => (
            <div key={i} style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "7px 10px",
              background: c === report.cheapest_competitor ? "rgba(184,213,141,0.07)" : "rgba(255,255,255,0.02)",
              borderRadius: 6, marginBottom: 4,
            }}>
              <span style={{ fontSize: 13, color: "#9b8f83" }}>
                {c === report.cheapest_competitor ? "⭐ " : ""}{c.source}
              </span>
              <span style={{ fontSize: 13, color: "#ffd6a5", fontWeight: 700 }}>
                AED {c.price.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Competitive gaps */}
      {ins.competitive_gaps?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            Competitive Gaps
          </div>
          {ins.competitive_gaps.map((g, i) => (
            <div key={i} style={{ fontSize: 12, color: "#ff9999", marginBottom: 4, display: "flex", gap: 6 }}>
              <span>⚠</span><span>{g}</span>
            </div>
          ))}
        </div>
      )}

      {/* Quick wins */}
      {ins.quick_wins?.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, color: "#666", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
            Quick Wins
          </div>
          {ins.quick_wins.map((w, i) => (
            <div key={i} style={{ fontSize: 12, color: "#b8d58d", marginBottom: 4, display: "flex", gap: 6 }}>
              <span>✓</span><span>{w}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const PricingDashboard: React.FC = () => {
  const [trainingOpen, setTrainingOpen] = useState(false);
  const [stats, setStats]         = useState<DashboardStats | null>(null);
  const [reports, setReports]     = useState<PriceReport[]>([]);
  const [loading, setLoading]     = useState(false);
  const [scanning, setScanning]   = useState(false);
  const [scanStatus, setScanStatus] = useState<{ running: boolean; last_scan: string | null; last_scan_count: number } | null>(null);
  const [selected, setSelected]   = useState<PriceReport | null>(null);
  const [filterDir, setFilterDir] = useState<string>("");
  const [filterBrand, setFilterBrand] = useState<string>("");
  const [search, setSearch]       = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch("/api/pricing/dashboard");
      if (r.ok) {
        const d = await r.json();
        setStats(d.stats);
        setReports(d.reports || []);
      }
    } catch {}
    setLoading(false);
  }, []);

  const pollScanStatus = useCallback(async () => {
    try {
      const r = await fetch("/api/pricing/agent/scan/status");
      if (r.ok) {
        const d = await r.json();
        setScanStatus(d);
        if (d.running) {
          setScanning(true);
          setTimeout(pollScanStatus, 3000);
        } else {
          setScanning(false);
          load();
        }
      }
    } catch {}
  }, [load]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      await fetch("/api/pricing/agent/scan", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ limit: 50 }) });
      setTimeout(pollScanStatus, 2000);
    } catch { setScanning(false); }
  };

  useEffect(() => { load(); pollScanStatus(); }, [load, pollScanStatus]);

  // Filtered & searched reports
  const visible = reports.filter(r => {
    if (filterDir && r.insight.price_change_direction !== filterDir) return false;
    if (filterBrand === "priority" && !r.is_priority_brand) return false;
    if (search && !r.product_title.toLowerCase().includes(search.toLowerCase()) &&
        !(r.vendor || "").toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const inputStyle: React.CSSProperties = {
    background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,214,165,0.15)",
    borderRadius: 7, padding: "7px 12px", color: "#ffd6a5", fontSize: 13, outline: "none",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0b1326 0%, #0e1830 50%, #0b1326 100%)",
      padding: "28px 24px",
      fontFamily: "'Manrope', 'Space Grotesk', sans-serif",
      color: "#ffd6a5",
    }}>
      <TrainingGuide moduleKey="pricing" steps={pricingSteps} isOpen={trainingOpen} onClose={() => setTrainingOpen(false)} />
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 28, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 900, color: "#ffd6a5" }}>
            Price Intelligence
          </h1>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#9b8f83" }}>
            AI-powered pricing analysis · Ollama → DeepSeek · ENZO &amp; LIZZE leverage applied
          </p>
          {scanStatus?.last_scan && (
            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#444" }}>
              Last scan: {fmtDate(scanStatus.last_scan)} · {scanStatus.last_scan_count} products
            </p>
          )}
          </div>
          <button onClick={() => setTrainingOpen(true)} title="Open training guide" style={{ background: "rgba(255,214,165,0.1)", border: "1px solid rgba(255,214,165,0.2)", borderRadius: 8, color: "#ffd6a5", cursor: "pointer", fontSize: 14, fontWeight: 700, padding: "3px 10px", alignSelf: "flex-start", flexShrink: 0 }}>?</button>
        </div>
        <button
          onClick={triggerScan}
          disabled={scanning}
          style={{
            background: scanning ? "rgba(255,214,165,0.1)" : "linear-gradient(135deg, #c9a96e, #e7c090)",
            border: "none", borderRadius: 9, padding: "10px 22px",
            color: scanning ? "#9b8f83" : "#0b1326",
            fontWeight: 800, fontSize: 13, cursor: scanning ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 8,
          }}
        >
          {scanning ? (
            <><span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span> Scanning…</>
          ) : "Run AI Scan"}
        </button>
      </div>

      {/* Stats row */}
      {stats && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
          <StatCard label="Products" value={stats.total_products} icon="📦" sub={`${stats.priority_brands} priority brands`} />
          <StatCard label="Avg Score" value={stats.avg_price_score + "/10"} icon="⭐" color={scoreColor(stats.avg_price_score)} />
          <StatCard label="Raise Price" value={stats.suggest_increase} icon="↑" color="#b8d58d" sub="below market" />
          <StatCard label="Lower Price" value={stats.suggest_decrease} icon="↓" color="#ff6b6b" sub="above competitors" />
          <StatCard label="Hold" value={stats.suggest_maintain} icon="→" color="#7ec8e3" sub="optimal range" />
        </div>
      )}

      {/* Market position mini bar */}
      {stats && stats.total_products > 0 && (
        <div style={{
          background: "rgba(255,255,255,0.02)", border: "0.5px solid rgba(189,178,255,0.12)",
          backdropFilter: "blur(60px)", WebkitBackdropFilter: "blur(60px)",
          borderRadius: 14, padding: "14px 18px", marginBottom: 24,
          display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center",
        }}>
          <span style={{ fontSize: 12, color: "#666", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Market Position
          </span>
          {(["below_market", "at_market", "above_market", "premium"] as const).map(pos => {
            const count = stats[pos === "below_market" ? "below_market"
              : pos === "at_market" ? "at_market"
              : pos === "above_market" ? "above_market"
              : "premium"];
            if (!count) return null;
            return (
              <div key={pos} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  width: 8, height: 8, borderRadius: "50%",
                  background: POSITION_COLORS[pos], display: "inline-block",
                }} />
                <span style={{ fontSize: 13, color: POSITION_COLORS[pos], fontWeight: 600 }}>
                  {count} {POSITION_LABELS[pos]}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input
          placeholder="Search products…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, minWidth: 200 }}
        />
        <select
          value={filterDir}
          onChange={e => setFilterDir(e.target.value)}
          style={{ ...inputStyle }}
        >
          <option value="">All directions</option>
          <option value="increase">↑ Raise price</option>
          <option value="decrease">↓ Lower price</option>
          <option value="maintain">→ Maintain</option>
        </select>
        <select
          value={filterBrand}
          onChange={e => setFilterBrand(e.target.value)}
          style={{ ...inputStyle }}
        >
          <option value="">All brands</option>
          <option value="priority">ENZO / LIZZE only</option>
        </select>
        {(filterDir || filterBrand || search) && (
          <button
            onClick={() => { setFilterDir(""); setFilterBrand(""); setSearch(""); }}
            style={{ ...inputStyle, cursor: "pointer", color: "#9b8f83", background: "none" }}
          >
            Clear filters
          </button>
        )}
        <span style={{ fontSize: 12, color: "#555", marginLeft: "auto" }}>
          {visible.length} of {reports.length} products
        </span>
      </div>

      {/* Reports list */}
      {loading ? (
        <div style={{ textAlign: "center", padding: "60px 0", color: "#9b8f83", fontSize: 14 }}>
          Loading price data…
        </div>
      ) : visible.length === 0 ? (
        <div style={{
          textAlign: "center", padding: "60px 24px",
          background: "rgba(255,255,255,0.02)", borderRadius: 14,
          border: "0.5px dashed rgba(189,178,255,0.15)",
        }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>💰</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#ffd6a5", marginBottom: 8 }}>
            {reports.length === 0 ? "No scan data yet" : "No products match filters"}
          </div>
          <div style={{ fontSize: 13, color: "#9b8f83" }}>
            {reports.length === 0
              ? "Click Run AI Scan to analyse your Shopify catalogue"
              : "Try clearing the filters"}
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {visible.map(r => (
            <ReportCard key={r.id} report={r} onSelect={setSelected} />
          ))}
        </div>
      )}

      {/* Detail drawer */}
      {selected && (
        <>
          <div
            onClick={() => setSelected(null)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 999,
            }}
          />
          <DetailDrawer report={selected} onClose={() => setSelected(null)} />
        </>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        select option { background: #0e1526; color: #ffd6a5; }
      `}</style>
    </div>
  );
};

export default PricingDashboard;

